import {
  DataStatus,
  openDialog,
  Table,
  TableRow,
  TableRowAction,
  TextInput,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {DeactivateBranchDialogContent} from '../../components/DeactivateBranchDialogContent/DeactivateBranchDialogContent';
import {MANAGER_ROLE_NAME} from '../UserDetail/constants/MANAGER_ROLE_NAME';
import {TableCell} from './components/TableCell';
import {TableHeader} from './components/TableHeader';

const columns = [
  {
    element: <TableHeader title={i18n.t('page.settings.labels.marketingName')} />,
    id: 'marketingName',
  },
  {
    element: <TableHeader title={i18n.t('entity.address.labels.city')} />,
    id: 'city',
  },
  {
    element: <TableHeader title={i18n.t('entity.address.labels.street')} />,
    id: 'street',
  },
  {
    element: <TableHeader title={i18n.t('entity.customer.labels.companyName')} />,
    id: 'companyName',
  },
  {
    element: <TableHeader title={i18n.t('entity.user.labels.users')} />,
    id: 'usersCount',
  },
  {
    element: <TableHeader title={i18n.t('entity.vehicle.labels.externalId')} />,
    id: 'externalId',
  },
  {
    element: null,
    id: 'actions',
  },
];

export function BranchOverview() {
  const navigate = useNavigate();
  const [externalIdFilter, setExternalIdFilter] = useState<string | null>(null);
  const {data: branches, isLoading, isError} = useGetBranchListQuery();
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const handleDeleteBranch = (branchId: string, branchName: string | null) => {
    openDialog(<DeactivateBranchDialogContent branchId={branchId} />, {
      id: 'deleteBranchDialog',
      'data-testid': 'deleteBranchDialog',
      title: i18n.t('page.settings.labels.deactivateBranchTitle', {name: branchName ?? ''}),
    });
  };

  const handleCreateNewBranch = () => navigate(settingsRoutes.branchesNew);

  const filteredBranches = branches?.branchListItems.filter(
    (branch) =>
      !externalIdFilter ||
      (branch.externalId?.toLowerCase().includes(externalIdFilter.toLowerCase()) ?? false)
  );

  const isUserAdmin = isNil(currentUser?.roles)
    ? false
    : currentUser.roles.some((role) => role.title === MANAGER_ROLE_NAME);

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('entity.branch.labels.branches'),
        actions: [
          {
            type: 'button',
            leftIcon: 'content/add_circle',
            title: i18n.t('entity.branch.actions.addBranch'),
            onClick: handleCreateNewBranch,
          },
        ],
      }}
      data-testid={testIds.settings.branches('branches-template')}
      isError={isError}
      isLoading={isLoading}
    >
      <VStack spacing={4}>
        <TextInput
          type="text"
          label={i18n.t('entity.vehicle.labels.externalId')}
          value={externalIdFilter}
          onChange={setExternalIdFilter}
        />
        <DataStatus
          isEmpty={filteredBranches?.length === 0}
          emptyMessage={i18n.t('general.labels.noResultsFound')}
        >
          <Table columns={columns}>
            {filteredBranches?.map((row) => {
              const editBranch = () =>
                navigate(composePath(settingsRoutes.branchesEdit, {params: {id: row.id}}));

              const actions = buildArray<TableRowAction>([
                {
                  icon: 'image/edit',
                  onClick: editBranch,
                  title: i18n.t('general.actions.edit'),
                },
              ]).when(isUserAdmin, {
                icon: 'action/delete',
                title: i18n.t('general.actions.delete'),
                onClick: () => {
                  handleDeleteBranch(row.id, row.marketingName);
                },
              });

              return (
                <TableRow
                  key={row.id}
                  onClick={editBranch}
                  actions={{
                    secondary: actions,
                  }}
                >
                  <TableCell value={row.marketingName} />
                  <TableCell value={row.city} />
                  <TableCell value={row.street} />
                  <TableCell value={row.companyName} />
                  <TableCell value={row.usersCount?.toString()} />
                  <TableCell value={row.externalId} />
                </TableRow>
              );
            })}
          </Table>
        </DataStatus>
      </VStack>
    </SettingsTemplate>
  );
}
